import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { CalendarRange, Loader2, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1573339887617-d674bc961c31?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

function Shell({ children, title, subtitle }) {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Produção de eventos" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/70 to-blue-900/60" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
              <CalendarRange className="h-6 w-6" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight">Mundideias CRM</span>
          </div>
          <div className="space-y-4">
            <h2 className="font-display text-4xl font-extrabold leading-tight max-w-md">
              Todos os seus orçamentos de eventos num só lugar.
            </h2>
            <p className="text-slate-300 max-w-sm text-base">
              Importe ficheiros Excel, consolide processos e acompanhe valor, custos e margem em tempo real.
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 relative">
        <button onClick={toggle} data-testid="theme-toggle-auth"
          className="absolute top-5 right-5 p-2 rounded-lg hover:bg-muted transition-colors">
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <CalendarRange className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-lg font-extrabold">Mundideias CRM</span>
          </div>
          <div className="space-y-1.5">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

export function LoginScreen() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Sessão iniciada");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Entrar" subtitle="Aceda ao seu painel de gestão de eventos.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@empresa.pt" required data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Palavra-passe</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
              Esqueceu-se?
            </Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" required data-testid="login-password-input" />
        </div>
        <Button type="submit" className="w-full h-11 text-base active:scale-[0.98] transition-transform"
          disabled={loading} data-testid="login-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
        </Button>
      </form>
    </Shell>
  );
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Repor palavra-passe" subtitle="Enviaremos um link para o seu email.">
      {sent ? (
        <div className="space-y-4" data-testid="forgot-password-sent">
          <p className="text-sm text-muted-foreground">
            Se esse email estiver registado, foi enviado um link de reposição. Verifique a sua caixa de entrada.
          </p>
          <Link to="/login" className="text-sm text-primary hover:underline">← Voltar a entrar</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fp-email">Email</Label>
            <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@empresa.pt" required data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="forgot-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar link"}
          </Button>
          <Link to="/login" className="block text-center text-sm text-primary hover:underline">← Voltar a entrar</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Palavra-passe reposta. Já pode entrar.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Nova palavra-passe" subtitle="Defina uma nova palavra-passe para a sua conta.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="rp-password">Nova palavra-passe</Label>
          <Input id="rp-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" required minLength={6} data-testid="reset-password-input" />
        </div>
        <Button type="submit" className="w-full h-11" disabled={loading} data-testid="reset-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Repor palavra-passe"}
        </Button>
        <Link to="/login" className="block text-center text-sm text-primary hover:underline">← Voltar a entrar</Link>
      </form>
    </Shell>
  );
}
